/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/outline';
import router from 'next/router';

import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import 'firebase/analytics';
import 'firebase/storage';
import ConnectTwitter from '../Modal/connect-twitter';
import ReconnectTwitter from '../Modal/reconnect-twitter';
import EssayCard from '../Cards/essayCard';

import SpinnerIcon from '../Icons/spinner';

import { RadioGroup } from '@headlessui/react';
import ConfirmDelete from '../Modal/confirm-delete';
import Button from '../Button';
import IconButton from '../Button/IconButton';
import { XIcon } from '@heroicons/react/solid';

const backgrounds = [
  { id: 0, bg: 'bg-white border border-gray-200', text: 'text-gray-900' },
  {
    id: 1,
    bg: 'bg-gradient-to-b from-gray-200 to-gray-300',
    text: 'text-gray-900',
  },
  {
    id: 2,
    bg: 'bg-gradient-to-b from-gray-900 to-gray-800',
    text: 'text-white',
  },
  {
    id: 3,
    bg: 'bg-gradient-to-b from-blue-500 to-indigo-600',
    text: 'text-white',
  },
  {
    id: 4,
    bg: 'bg-gradient-to-b from-red-400 to-orange-400',
    text: 'text-white',
  },
  {
    id: 5,
    bg: 'bg-gradient-to-b from-yellow-400 to-yellow-500',
    text: 'text-white',
  },
  {
    id: 6,
    bg: 'bg-gradient-to-b from-pink-500 to-indigo-500',
    text: 'text-white',
  },
  {
    id: 7,
    bg: 'bg-gradient-to-b from-green-500 to-green-600',
    text: 'text-white',
  },
];

export default function CreateCollectionModal({
  open,
  setOpen,
  card,
  refreshCards,
  loggedInUserID,
  editing = null,
  userSlug,
}) {
  const cancelButtonRef = useRef();

  const [title, setTitle] = useState('');
  const [background, setBackground] = useState(0);
  const [description, setDescription] = useState('');
  const [collectionSlug, setCollectionSlug] = useState(null);
  const [newPhotoURL, setNewPhotoURL] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [uploadedIMG, setUploadedIMG] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);

  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [titleChanged, setTitleChanged] = useState(false);

  const [buttonLoading, setButtonLoading] = useState(false);

  useEffect(() => {
    setButtonLoading(false);

    if (card !== null) {
      setTitle(card.title);
      setDescription(card.description);
      setCollectionSlug(card.slug);
      card.image !== undefined ? getImage(card.slug, card.image.image) : null;
      setShowDelete(true);
      setTitleChanged(false);
    } else {
      setTitle('');
      setDescription('');
      setCollectionSlug(null);
      setShowDelete(false);
      setUploadedIMG(null);
      setImageChanged(false);
      setTitleChanged(false);
    }
  }, [card, open]);

  useEffect(() => {
    //also have to make sure slugs arent duplicates
    if (card === null) {
      setCollectionSlug(title.toLocaleLowerCase().replace(/[^0-9a-z]/gi, ''));
      setTitleChanged(true);
    } else {
    }
  }, [title]);

  const saveTextCard = () => {
    setButtonLoading(true);
    if (title.length > 1 && description.length > 1 && uploadedIMG !== null) {
      if (titleChanged) {
        const pathRef = firebase
          .database()
          .ref('paths/collections')
          .child(loggedInUserID)
          .child('collections')
          .child(collectionSlug);
        pathRef.once('value', (querySnapshot) => {
          if (querySnapshot.exists()) {
            alert('A collection with that URL already exists');
            setButtonLoading(false);
          } else {
            firebase
              .database()
              .ref('gallery')
              .child(userSlug)
              .child(loggedInUserID)
              .child('collections')
              .child(collectionSlug)
              .child('meta')
              .update({
                title,
                background,
                description,
                slug: collectionSlug,
              })
              .then(() => {
                firebase
                  .database()
                  .ref('paths/collections')
                  .child(loggedInUserID)
                  .child('collections')
                  .child(collectionSlug)
                  .set(true)
                  .then(() => {
                    firebase
                      .database()
                      .ref('paths/collections')
                      .child(loggedInUserID)
                      .update({
                        slug: userSlug,
                      })
                      .then(
                        imageChanged
                          ? addImageToUserDB(uploadedIMG)
                          : refreshCards({
                              title,
                              background,
                              description,
                              slug: collectionSlug,
                              image: {
                                image: card.image.image,
                              },
                            }),
                      );
                  });
              });
          }
        });
      } else {
        firebase
          .database()
          .ref('gallery')
          .child(userSlug)
          .child(loggedInUserID)
          .child('collections')
          .child(collectionSlug)
          .child('meta')
          .update({
            title,
            background,
            description,
            slug: collectionSlug,
          })
          .then(() => {
            firebase
              .database()
              .ref('paths/collections')
              .child(loggedInUserID)
              .child('collections')
              .child(collectionSlug)
              .set(true)
              .then(() => {
                firebase
                  .database()
                  .ref('paths/collections')
                  .child(loggedInUserID)
                  .update({
                    slug: userSlug,
                  })
                  .then(
                    imageChanged
                      ? addImageToUserDB(uploadedIMG)
                      : refreshCards({
                          title,
                          background,
                          description,
                          slug: collectionSlug,
                          image: {
                            image: card.image.image,
                          },
                        }),
                  );
              });
          });
      }
    } else {
      alert('A cover photo, title, and description are all required.');
      setButtonLoading(false);
    }
  };

  const deleteCollection = () => {
    firebase
      .database()
      .ref('gallery')
      .child(userSlug)
      .child(loggedInUserID)
      .child('collections')
      .child(collectionSlug)
      .remove()
      .then(() => {
        firebase
          .database()
          .ref('paths/collections')
          .child(loggedInUserID)
          .child('collections')
          .child(collectionSlug)
          .remove()
          .then(() => {
            refreshCards(null, true);
            setConfirmDeleteModal(false);
          });
      });
  };

  const getImage = (colSlug, imageKey) => {
    var storageRef2 = firebase.storage().ref();
    var imgRef = storageRef2
      .child(loggedInUserID)
      .child('collections')
      .child(colSlug)
      .child(`${imageKey}`);

    imgRef.getDownloadURL().then((url) => {
      var image = document.getElementById('cover-photo');
      if (image !== null) {
        image.src = url;
        setUploadedIMG(image);
      }
    });
  };

  const uploadImage = (event) => {
    if (event.target.files && event.target.files[0]) {
      if (event.target.files[0].size > 2097152) {
        alert('Maximum image size is 2MB');
      } else {
        let img = event.target.files[0];
        setUploadedIMG(img);
        setImageChanged(true);

        var image = document.getElementById('cover-photo');
        image.src = URL.createObjectURL(event.target.files[0]);

        // document.getElementById("image-upload").value = '';
      }
    }
  };

  const addImageToUserDB = (image) => {
    const dbRef = firebase
      .database()
      .ref('gallery')
      .child(userSlug)
      .child(loggedInUserID)
      .child('collections')
      .child(collectionSlug)
      .child('meta')
      .child('image');
    const newRef = dbRef.push();
    dbRef
      .update({
        image: newRef.key,
      })
      .then(() => {
        var key = newRef.key;
        //add image file to firebase database then grab key and upload to storage
        var storageRef = firebase.storage().ref();
        var upload = storageRef
          .child(loggedInUserID)
          .child('collections')
          .child(collectionSlug)
          .child(key)
          .put(image);

        upload.then((snapshot) => {
          refreshCards({
            title,
            background,
            description,
            slug: collectionSlug,
            image: {
              image: newRef.key,
            },
          });
        });
      });
  };

  return (
    <>
      <ConfirmDelete
        open={confirmDeleteModal}
        setOpen={setConfirmDeleteModal}
        deleteConfirmed={deleteCollection}
        keep={() => setConfirmDeleteModal(false)}
      />
      <Transition.Root
        initialFocus={cancelButtonRef}
        static
        show={open}
        as={Fragment}
      >
        <Dialog
          as="div"
          static
          className="fixed inset-0 z-40 overflow-y-auto"
          open={open}
          onClose={setOpen}
        >
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center antialiased sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-md backdrop-filter transition-all" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:h-screen sm:align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-24 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-24 sm:scale-95"
            >
              <div
                className={`relative inline-block w-full transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all sm:my-8 sm:max-w-lg sm:align-middle`}
              >
                <div className={'absolute top-2 right-2'}>
                  <IconButton
                    click={() => setOpen(false)}
                    ref={cancelButtonRef}
                    variant={'outline'}
                    prefix={<XIcon className={'h-4 w-4'} />}
                  />
                </div>

                <div
                  className={'flex flex-col items-start justify-center pt-8'}
                >
                  <Dialog.Title
                    as="h3"
                    className="mb-8 px-8 text-left font-primary text-3xl font-bold text-gray-900"
                  >
                    {editing ? 'Edit Collection' : 'Create a Collection'}
                  </Dialog.Title>

                  <div
                    className={
                      'flex w-full flex-col items-center justify-center space-y-6 px-8 pb-8'
                    }
                  >
                    <div className={'flex w-full flex-col space-y-1'}>
                      <p
                        className={
                          'font-primary text-lg font-semibold text-gray-900'
                        }
                      >
                        Cover Photo
                      </p>

                      <div className="mx-auto mt-1 w-full">
                        <label
                          htmlFor="file-upload"
                          className=" flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6 font-primary text-base font-semibold"
                        >
                          <input
                            id="image-upload"
                            onChange={(event) => uploadImage(event)}
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                          />
                          <img
                            id="cover-photo"
                            className={` ${
                              uploadedIMG ? 'flex' : 'hidden'
                            } h-32 w-32 rounded-3xl`}
                          />
                          <div
                            className={` ${
                              uploadedIMG ? 'hidden' : 'flex'
                            } flex-col items-center justify-center space-y-1 text-center`}
                          >
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div className="flex text-sm text-gray-600">
                              <span className={'text-indigo-600'}>
                                Upload a Cover Photo
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              Image will be cropped to square
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className={'flex w-full flex-col space-y-1'}>
                      <p
                        className={
                          'font-primary text-lg font-semibold text-gray-900'
                        }
                      >
                        Title
                      </p>
                      <div className="mt-1 w-full">
                        <input
                          value={title}
                          placeholder={'My Collection Title'}
                          onChange={(event) => setTitle(event.target.value)}
                          className={
                            'w-full rounded-lg border-2 border-gray-200 bg-white p-3 font-medium transition duration-200 focus:border-gray-900 focus:outline-none focus:ring-0'
                          }
                        />
                      </div>
                    </div>

                    <div className={'flex w-full flex-col space-y-1'}>
                      <p
                        className={
                          'font-primary text-lg font-semibold text-gray-900'
                        }
                      >
                        Description
                      </p>
                      <div className="mt-1 w-full">
                        <textarea
                          placeholder={
                            'This is a collection description and goes under the title.'
                          }
                          value={description}
                          onChange={(event) =>
                            setDescription(event.target.value)
                          }
                          id="bio"
                          name="bio"
                          rows={4}
                          maxLength={160}
                          className={
                            'w-full rounded-lg border-2 border-gray-200 bg-white p-3 font-medium transition duration-200 focus:border-gray-900 focus:outline-none focus:ring-0'
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={`${
                      showDelete
                        ? 'space-x-0 space-y-2 space-y-reverse sm:space-x-4 sm:space-y-0'
                        : 'px-8'
                    } flex w-full flex-col-reverse bg-gray-100 p-4 sm:flex-row `}
                  >
                    {showDelete ? (
                      <Button
                        width={'full'}
                        variant={'error-secondary'}
                        click={() => setConfirmDeleteModal(true)}
                      >
                        Delete Collection
                      </Button>
                    ) : null}

                    <Button
                      click={saveTextCard}
                      disabled={buttonLoading}
                      loading={buttonLoading}
                      width={'full'}
                    >
                      {editing ? 'Save Changes' : 'Create Collection'}
                    </Button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
